import {
  QueryClient,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
} from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetchClient } from '@/api/fetch-client';
import {
  CreateReportFindingError,
  CreateReportFindingRequest,
  CreateReportFindingResponse,
  PatchReportFindingError,
  PatchReportFindingRequest,
  PatchReportFindingResponse,
  ReportFindingsByReportError,
  ReportFindingsByReportRequest,
  ReportFindingsByReportResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';

const KEY = {
  GET_REPORT_FINDING_REPORT: 'GET_REPORT_FINDING_REPORT',
} as const;

const request = {
  getReportFindingsByReport: async (
    { payload: { params, uuid } }: ReportFindingsByReportRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetchClient.FCT.get<ReportFindingsByReportResponse>(
      `${ENDPOINTS.FCT.REPORT_FINDING.REPORT}${uuid}`,
      { params: removeEmptyParams(params), signal },
    );

    return data;
  },
  patchReportFinding: async ({ payload }: PatchReportFindingRequest) => {
    const { data } = await fetchClient.FCT.patch<PatchReportFindingResponse>(
      `${ENDPOINTS.FCT.REPORT_FINDING.BASE}${payload.uuid}`,
      { body: payload },
    );

    return data;
  },
  postCreateReportFinding: async ({ payload }: CreateReportFindingRequest) => {
    const { data } = await fetchClient.FCT.post<CreateReportFindingResponse>(
      ENDPOINTS.FCT.REPORT_FINDING.BASE,
      { body: payload },
    );

    return data;
  },
};

const query = {
  useGetReportFindingsByReport: (
    { payload }: ReportFindingsByReportRequest,
    config?: Partial<
      UseInfiniteQueryOptions<ReportFindingsByReportResponse, ReportFindingsByReportError>
    >,
  ) =>
    useInfiniteQuery({
      enabled: !!payload.uuid && !!payload.params.user_id,
      getNextPageParam: (lastPage) => {
        const size = payload.params.size ?? 20;

        return lastPage.items.length < size ? undefined : lastPage.page + 1;
      },
      initialPageParam: 0,
      queryFn: ({ pageParam = 0, signal }) =>
        request.getReportFindingsByReport(
          {
            payload: {
              params: { ...payload.params, page: pageParam as number, size: payload.params.size },
              uuid: payload.uuid,
            },
          },
          signal,
        ),
      queryKey: [KEY.GET_REPORT_FINDING_REPORT, payload.uuid, payload.params.user_id],
      ...config,
    }),
};

const mutation = {
  usePatchReportFinding: (queryClient?: QueryClient) =>
    useMutation<PatchReportFindingResponse, PatchReportFindingError, PatchReportFindingRequest>({
      mutationFn: request.patchReportFinding,
      onSuccess: (value) => {
        if (queryClient) {
          queryClient.invalidateQueries({ queryKey: [KEY.GET_REPORT_FINDING_REPORT] });
        }

        return value;
      },
    }),
  usePostCreateReportFinding: () =>
    useMutation<CreateReportFindingResponse, CreateReportFindingError, CreateReportFindingRequest>({
      mutationFn: request.postCreateReportFinding,
    }),
};

export const reportFinding = {
  KEY,
  mutation,
  query,
  request,
};
